import React, { useEffect, useState, useRef } from "react";
import { VectorCandleEvent } from "../../lib/interface/VectorCandleEvent.interface";
import {
  IChartApi,
  ISeriesApi,
  Time,
  MouseEventParams,
  createSeriesMarkers,
  SeriesMarker,
  ISeriesMarkersPluginApi,
} from "lightweight-charts";
import type { WSDeltaTrendPayload } from "@lacon/ws";
import { DeltaLabelKind } from "@lacon/ws";
import { debugLog, debugWarn } from "@/lib/utils/debug";

interface VectorCandleMarkersProps {
  vectorCandles: VectorCandleEvent[];
  chartInstance: IChartApi | null;
  candleSeries: ISeriesApi<"Candlestick"> | null;
  showVectorCandles: boolean;
  // DeltaTrend markers merge
  deltaTrend?: WSDeltaTrendPayload | null;
  showDeltaTrend?: boolean;
}

interface MarkerPosition {
  x: number;
  y: number;
  vectorCandle: VectorCandleEvent;
  visible: boolean;
}

export const VectorCandleMarkers: React.FC<VectorCandleMarkersProps> = ({
  vectorCandles,
  chartInstance,
  candleSeries,
  showVectorCandles,
  deltaTrend = null,
  showDeltaTrend = false,
}) => {
  const [markers, setMarkers] = useState<MarkerPosition[]>([]);
  const [hoveredMarker, setHoveredMarker] = useState<VectorCandleEvent | null>(
    null
  );
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const seriesMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null); // Store reference to the markers API

  // Initialize the series markers API ONCE when component mounts
  useEffect(() => {
    if (!chartInstance || !candleSeries) {
      return;
    }

    // Create the series markers API reference only once
    if (!seriesMarkersRef.current) {
      try {
        seriesMarkersRef.current = createSeriesMarkers(candleSeries, []);
        debugLog("VectorCandleMarkers: created series markers API reference");
      } catch (error) {
        debugWarn("VectorCandleMarkers: failed to create markers API:", error);
        return;
      }
    }
    return () => {
      if (seriesMarkersRef.current) {
        try {
          seriesMarkersRef.current.setMarkers([]);
        } catch (error) {
          debugWarn(
            "VectorCandleMarkers: failed to clear markers on cleanup:",
            error
          );
        }
      }
      seriesMarkersRef.current = null;
    };
  }, [chartInstance, candleSeries]);

  // Update markers when vectorCandles or deltaTrend or toggles change
  useEffect(() => {
    if (!chartInstance || !candleSeries || !seriesMarkersRef.current) {
      return;
    }

    // Always clear markers first
    setMarkers([]);

    const showAny = showVectorCandles || showDeltaTrend;

    // If both are disabled, clear native markers and exit
    if (!showAny) {
      try {
        seriesMarkersRef.current.setMarkers([]); // Use the API to clear markers
      } catch (error) {
        debugWarn("VectorCandleMarkers: failed to clear markers:", error);
      }
      return;
    }

    // Build vector candle markers (if enabled)
    const validSignals = showVectorCandles
      ? vectorCandles.filter((vc) => {
          const action = vc.action ?? vc.signal;
          return action === "BUY" || action === "SELL";
        })
      : [];

    const chartMarkers = validSignals.map((vectorCandle) => {
      const timeInSeconds =
        vectorCandle.timestamp > 9999999999
          ? Math.floor(vectorCandle.timestamp / 1000)
          : Math.floor(vectorCandle.timestamp);
      const time = timeInSeconds as Time;

      // Map vectorType color names to hex colors (same as candle colors)
      const colorMap: Record<string, string> = {
        GREEN: "#22c55e", // Green
        RED: "#ef4444", // Red
        BLUE: "#3b82f6", // Blue
        VIOLET: "#8b5cf6", // Violet
      };

      const markerColor = colorMap[vectorCandle.vectorType] || "#6b7280"; // Default to grey if unknown

      const action = vectorCandle.action ?? vectorCandle.signal;

      return {
        time: time,
        position:
          action === "BUY" ? ("belowBar" as const) : ("aboveBar" as const),
        color: markerColor,
        shape: action === "BUY" ? ("arrowUp" as const) : ("arrowDown" as const),
        text: action === "BUY" ? "B" : "S",
      };
    });

    // Build DeltaTrend markers (dots + labels) if enabled
    const deltaMarkers: SeriesMarker<Time>[] = [];
    if (showDeltaTrend && deltaTrend) {
      const addTime = (ts: number): Time =>
        (ts > 9999999999 ? Math.floor(ts / 1000) : Math.floor(ts)) as Time;

      for (const d of deltaTrend.dots || []) {
        deltaMarkers.push({
          time: addTime(d.timestamp),
          position:
            d.type === "UP" ? ("belowBar" as const) : ("aboveBar" as const),
          color: d.type === "UP" ? "#10b981" : "#ef4444",
          shape:
            d.type === "UP" ? ("arrowUp" as const) : ("arrowDown" as const),
          text: "",
        });
      }
      for (const l of deltaTrend.labels || []) {
        deltaMarkers.push({
          time: addTime(l.timestamp),
          position:
            l.kind === DeltaLabelKind.TOP
              ? ("aboveBar" as const)
              : ("belowBar" as const),
          color: l.kind === DeltaLabelKind.TOP ? "#fbbf24" : "#22c55e",
          shape: "circle" as const,
          text: l.kind === DeltaLabelKind.TOP ? "T" : "B",
        });
      }
    }

    // Use setMarkers to update the existing markers API
    try {
      seriesMarkersRef.current.setMarkers([...chartMarkers, ...deltaMarkers]);
      setMarkers([]); // Clear custom markers since we're using native ones
    } catch (error) {
      debugWarn("VectorCandleMarkers: failed to update markers:", error);
      setMarkers([]);
    }

    // Set up tooltip handling
    const handleCrosshairMove = (param: MouseEventParams<Time>) => {
      if (!param.time) {
        setHoveredMarker(null);
        return;
      }

      // Find vector candle at this time
      const foundVectorCandle = validSignals.find((vc) => {
        const vcTimeInSeconds =
          vc.timestamp > 9999999999
            ? Math.floor(vc.timestamp / 1000)
            : Math.floor(vc.timestamp);
        return Math.abs(vcTimeInSeconds - (param.time as number)) < 60; // Within 1 minute
      });

      if (foundVectorCandle && param.point) {
        setHoveredMarker(foundVectorCandle);
        setTooltipPosition({
          x: param.point.x,
          y: param.point.y,
        });
      } else {
        setHoveredMarker(null);
      }
    };

    chartInstance.subscribeCrosshairMove(handleCrosshairMove);

    return () => {
      chartInstance.unsubscribeCrosshairMove(handleCrosshairMove);
    };
  }, [
    vectorCandles,
    showVectorCandles,
    deltaTrend,
    showDeltaTrend,
    chartInstance,
    candleSeries,
  ]);

  // Cleanup effect to clear markers when component unmounts
  useEffect(() => {
    return () => {
      if (seriesMarkersRef.current) {
        try {
          seriesMarkersRef.current.setMarkers([]); // Clear all native markers on unmount
        } catch (error) {
          debugWarn(
            "VectorCandleMarkers: failed to clear markers on unmount:",
            error
          );
        }
      }
      seriesMarkersRef.current = null;
    };
  }, []);

  const handleMarkerHover = (
    vectorCandle: VectorCandleEvent,
    event: React.MouseEvent
  ) => {
    setHoveredMarker(vectorCandle);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMarkerLeave = () => {
    setHoveredMarker(null);
  };

  if (markers.length === 0 && vectorCandles.length > 0) {
    // Using native markers only; still render tooltip when hovering
    return (
      <>
        {hoveredMarker && (
          <div
            className="fixed z-50 bg-gray-900 text-white p-3 rounded-lg shadow-xl border border-gray-700 pointer-events-none min-w-48"
            style={{
              left: `${tooltipPosition.x + 10}px`,
              top: `${tooltipPosition.y - 10}px`,
              maxWidth: "280px",
            }}
          >
            <div className="text-sm space-y-1">
              <div
                className="font-bold text-lg"
                style={{
                  color: (() => {
                    const colorMap: Record<string, string> = {
                      GREEN: "#22c55e",
                      RED: "#ef4444",
                      BLUE: "#3b82f6",
                      VIOLET: "#8b5cf6",
                    };
                    return colorMap[hoveredMarker.vectorType] || "#6b7280";
                  })(),
                }}
              >
                {hoveredMarker.action ?? hoveredMarker.signal ?? ""} SIGNAL
              </div>
              <div className="text-gray-300 font-medium">
                {hoveredMarker.vectorType}
              </div>
              <div className="border-t border-gray-600 pt-2 space-y-1">
                <div className="text-yellow-400">
                  Confidence: {(hoveredMarker.confidence * 100).toFixed(1)}%
                </div>
                <div className="text-blue-400">
                  Speed Ratio: {hoveredMarker.speedRatio.toFixed(2)}
                </div>
                <div className="text-purple-400">
                  Recovery: {hoveredMarker.priceRecovery.toFixed(4)}
                </div>
              </div>
              <div className="text-xs text-gray-400 pt-2 border-t border-gray-600">
                {(() => {
                  let dateToDisplay: Date;
                  if (hoveredMarker.timestamp > 9999999999) {
                    dateToDisplay = new Date(hoveredMarker.timestamp);
                  } else {
                    dateToDisplay = new Date(hoveredMarker.timestamp * 1000);
                  }
                  if (isNaN(dateToDisplay.getTime())) {
                    return `Invalid timestamp: ${hoveredMarker.timestamp}`;
                  }
                  return dateToDisplay.toLocaleString();
                })()}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Always render the component to handle native markers and tooltips
  // Don't return null based on markers.length since we might be using native markers

  return (
    <>
      {/* no on-chart debug label */}

      {/* Marker Overlay - Positioned absolutely over the chart */}
      <div
        ref={containerRef}
        className="absolute inset-0 pointer-events-none z-20"
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
      >
        {markers.map((marker, index) => (
          <div
            key={`marker-${marker.vectorCandle.timestamp}-${index}`}
            className="absolute pointer-events-auto cursor-pointer"
            style={{
              left: `${marker.x}px`,
              top: `${marker.y}px`,
              transform: "translate(-50%, -50%)",
            }}
            onMouseEnter={(e) => handleMarkerHover(marker.vectorCandle, e)}
            onMouseLeave={handleMarkerLeave}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg hover:scale-125 transition-all duration-200 border-2 border-white animate-pulse"
              style={{
                backgroundColor: (() => {
                  // Map vectorType color names to hex colors (same as candle colors)
                  const colorMap: Record<string, string> = {
                    GREEN: "#22c55e", // Green
                    RED: "#ef4444", // Red
                    BLUE: "#3b82f6", // Blue
                    VIOLET: "#8b5cf6", // Violet
                  };
                  return colorMap[marker.vectorCandle.vectorType] || "#6b7280";
                })(),
              }}
              title={`${marker.vectorCandle.action ?? marker.vectorCandle.signal ?? ""} Signal - ${marker.vectorCandle.vectorType}`}
            >
              {(marker.vectorCandle.action ?? marker.vectorCandle.signal) ===
              "BUY"
                ? "B"
                : "S"}
            </div>
          </div>
        ))}
      </div>

      {/* Tooltip */}
      {hoveredMarker && (
        <div
          className="fixed z-50 bg-gray-900 text-white p-3 rounded-lg shadow-xl border border-gray-700 pointer-events-none min-w-48"
          style={{
            left: `${tooltipPosition.x + 10}px`,
            top: `${tooltipPosition.y - 10}px`,
            maxWidth: "280px",
          }}
        >
          <div className="text-sm space-y-1">
            <div
              className="font-bold text-lg"
              style={{
                color: (() => {
                  // Map vectorType color names to hex colors (same as candle colors)
                  const colorMap: Record<string, string> = {
                    GREEN: "#22c55e", // Green
                    RED: "#ef4444", // Red
                    BLUE: "#3b82f6", // Blue
                    VIOLET: "#8b5cf6", // Violet
                  };
                  return colorMap[hoveredMarker.vectorType] || "#6b7280";
                })(),
              }}
            >
              {hoveredMarker.action ?? hoveredMarker.signal ?? ""} SIGNAL
            </div>
            <div className="text-gray-300 font-medium">
              {hoveredMarker.vectorType}
            </div>
            <div className="border-t border-gray-600 pt-2 space-y-1">
              <div className="text-yellow-400">
                Confidence: {(hoveredMarker.confidence * 100).toFixed(1)}%
              </div>
              <div className="text-blue-400">
                Speed Ratio: {hoveredMarker.speedRatio.toFixed(2)}
              </div>
              <div className="text-purple-400">
                Recovery: {hoveredMarker.priceRecovery.toFixed(4)}
              </div>
            </div>
            <div className="text-xs text-gray-400 pt-2 border-t border-gray-600">
              {(() => {
                try {
                  // More robust timestamp conversion
                  let dateToDisplay: Date;

                  // Check if timestamp is in milliseconds (> 1 billion seconds since epoch)
                  if (hoveredMarker.timestamp > 1000000000000) {
                    // Timestamp is in milliseconds
                    dateToDisplay = new Date(hoveredMarker.timestamp);
                  } else if (hoveredMarker.timestamp > 1000000000) {
                    // Timestamp is in seconds, convert to milliseconds
                    dateToDisplay = new Date(hoveredMarker.timestamp * 1000);
                  } else {
                    // Invalid timestamp
                    return `Invalid timestamp: ${hoveredMarker.timestamp}`;
                  }

                  // Check if date is valid
                  if (isNaN(dateToDisplay.getTime())) {
                    return `Invalid date from timestamp: ${hoveredMarker.timestamp}`;
                  }

                  return dateToDisplay.toLocaleString();
                } catch {
                  return `Timestamp error: ${hoveredMarker.timestamp}`;
                }
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
